/**
 * English UI catalogue of the Library (W2-10 projects-data; namespace 'library').
 * Imports only src/i18n/define.ts (UI message catalogue zone). ICU-lite syntax (src/i18n/icu.ts).
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('library', {
  // ---- page ----
  appName: 'Phiedit',
  searchLabel: 'Search charts',
  searchPlaceholder: 'Search by title, artist or level',
  help: 'Help',
  settings: 'Settings',
  startLabel: 'Start',
  startNewTitle: 'New chart',
  startNewBody: 'Start from a song',
  startOpenTitle: 'Open a chart file',
  startOpenBody: '.pez .zip folder .json .pec',
  startSampleTitle: 'Learn with a sample',
  startSampleBody: '2-minute guided tour',
  nextStepTitle: 'Start here',
  nextStepBody:
    'Learn with a sample opens a ready-made chart and walks you through the editor in about 2 minutes. When you are ready, New chart starts from your own song.',
  nextStepDismiss: 'Hide this hint',
  startUnavailable: 'Not available in this build',
  dropHint: 'Or drop a song, a chart, a package or a skin anywhere on this page.',
  yourCharts: 'Your charts',
  chartCount: '{count, plural, one {# chart} other {# charts}}',
  groupBySong: 'Group by song',
  sortLabel: 'Sort',
  sortEdited: 'Last edited',
  sortOpened: 'Last opened',
  sortCreated: 'Newest first',
  sortName: 'Title',
  sortDifficulty: 'Difficulty',
  viewLabel: 'View',
  viewGrid: 'Grid',
  viewList: 'List',
  emptyTitle: 'No charts yet',
  emptyBody: 'Start a new chart from a song, open a chart file, or learn with the sample.',
  noMatchTitle: 'No chart matches "{query}"',
  noMatchBody: 'Search looks at titles, artists and levels.',
  clearSearch: 'Clear search',
  loading: 'Loading your charts...',
  loadFailed: 'Your charts could not be listed: {reason}',
  retry: 'Try again',
  listLabel: 'Charts',
  keyboardHint: 'Arrow keys move, Enter opens, F2 renames, Delete moves to the Trash, typing searches.',

  // ---- groups and cards ----
  songShared: '{count, plural, one {# chart of this song} other {# difficulties share this song}}',
  songGroupLabel: 'Song {title}',
  unknownComposer: 'Unknown artist',
  untitled: 'Untitled chart',
  noLevel: 'No level',
  stats: '{notes, plural, one {# note} other {# notes}} · {lines, plural, one {# line} other {# lines}}',
  edited: 'edited {when}',
  editedJustNow: 'edited just now',
  justNow: 'just now',
  changedSinceExport: 'changed since export',
  neverExported: 'not exported yet',
  exported: 'exported {when}',
  recovered: 'recovered unsaved edits',
  recoveredTip:
    'Phiedit was closed while this chart was open. Its latest edits were recovered from the journal: nothing is lost.',
  cardLabel: '{name}, {level}. {stats}. {edited}. {flags}.',
  thumbnailAlt: 'Illustration of {name}',
  menuLabel: 'Actions for {name}',
  actOpen: 'Open',
  actNewDifficulty: 'New difficulty (shares the song)...',
  actDuplicate: 'Duplicate',
  actExport: 'Export .pez',
  actVersions: 'Version history',
  actRename: 'Rename',
  actRecovery: 'Download recovery data',
  actTrash: 'Move to Trash (restorable for 30 days)',
  renameLabel: 'Chart title',
  renameEmpty: 'A chart needs a title.',
  renamed: 'Renamed to "{name}".',
  renameTxn: 'Rename chart to "{name}"',
  duplicated: 'Created "{name}".',
  copyName: '{name} (copy)',
  trashed: 'Moved "{name}" to the Trash. It is kept for 30 days.',
  restore: 'Restore',
  restored: 'Restored "{name}".',
  inOtherTab: '"{name}" is open in another tab. Close it there first.',
  notFound: 'This chart no longer exists.',
  quota: 'The browser has no space left for Phiedit. Free some space or delete charts you no longer need.',
  actionFailed: 'That did not work: {reason}',
  openFailed: '"{name}" could not be opened: {reason}',
  recoverySaved: 'Recovery data of "{name}" downloaded.',
  recoveryFileName: '{name} recovery data {date}',
  open: 'Open',
  busy: 'Working on it...',
  dismiss: 'Dismiss',
  exportOpening: 'Opening "{name}" to export it...',

  // ---- trash ----
  trashTitle: 'Trash',
  trashBack: 'Library',
  trashIntro: 'Charts in the Trash are deleted for good after 30 days. Restore a chart to edit it again.',
  trashEmptyTitle: 'The Trash is empty',
  trashEmptyBody: 'Charts you move to the Trash stay here for 30 days.',
  trashDaysLeft: '{days, plural, =0 {deleted today} one {deleted in # day} other {deleted in # days}}',
  trashedAt: 'moved to the Trash {when}',
  deleteForever: 'Delete forever',
  emptyTrash: 'Empty Trash',
  deleteOneTitle: 'Delete "{name}" forever?',
  deleteManyTitle: 'Delete {count} charts forever?',
  deleteBody: 'This cannot be undone. Files that no other chart uses are removed too.',
  deleteConfirm: 'Delete forever',
  deleted: '{count, plural, one {Deleted # chart for good.} other {Deleted # charts for good.}}',
  trashListLabel: 'Charts in the Trash',

  // ---- storage footer ----
  storageLabel: 'Storage',
  storageLoading: 'Checking storage...',
  storageUnknown: 'Storage use is not available in this browser.',
  sizeB: '{size} B',
  sizeKB: '{size} KB',
  sizeMB: '{size} MB',
  sizeGB: '{size} GB',
  sizeTB: '{size} TB',
  usage: '{used} of {quota} used',
  usageUnknown: '{used} used',
  protectedLine: 'Protected from automatic cleanup',
  notProtected: 'Not protected: the browser may delete your charts when space runs low.',
  protect: 'Protect my charts',
  protectGranted: 'Your charts are now protected from automatic cleanup.',
  protectDenied: 'The browser did not allow it. Download a backup now and then to stay safe.',
  lowSpace: 'Less than 200 MB free: Phiedit may soon be unable to save.',
  writeError: 'The last save failed: {reason}',
  trashLink: 'Trash ({count})',
  backup: 'Download all as backup',
  backupProgress: 'Preparing the backup',
  backupDone: '{count, plural, one {Backup downloaded: # chart.} other {Backup downloaded: # charts.}}',
  backupFailed: 'The backup could not be made: {reason}',
  backupNothing: 'There are no charts to back up yet.',
  backupFileName: 'Phiedit backup {date}',
  opfsOff: 'Media files are kept in the browser database (this browser has no private file system).',

  // ---- recovery notice ----
  recoveryTitle: '{count, plural, one {A chart was not closed properly} other {# charts were not closed properly}}',
  recoveryBody: 'Phiedit recovered their latest edits from its journal. Nothing is lost: open a chart to continue.',
  recoveryOpen: 'Open "{name}"',

  // ---- imports ----
  importing: 'Importing',
  importStageUnpack: 'Unpacking the files...',
  importStageManifest: 'Reading the package details...',
  importStageChart: 'Reading the chart...',
  importStageFiles: 'Reading the song and pictures...',
  importStageConvert: 'Converting the chart...',
  importStageCheck: 'Checking the chart...',
  importStageSave: 'Saving the project...',
  /** CR-9 (UX-088): the stage label with the package path the importer is on, e.g. "Reading the song and pictures... sample-song.wav". */
  importStageAt: '{stage} {file}',

  // ---- new difficulty ----
  ndTitle: 'New difficulty of "{name}"',
  ndDescription: 'The new chart shares the song and illustration. Tempo, offset and song details are copied.',
  ndStart: 'Start',
  ndEmpty: 'Start empty',
  ndEmptyHelp: 'No lines or notes: chart this difficulty from scratch.',
  ndCopy: 'Copy this chart',
  ndCopyHelp: 'Lines, notes and motion are copied: rework them for the new difficulty.',
  ndLabel: 'Difficulty label',
  ndLabelOther: 'Other',
  ndCustomLabel: 'Custom label',
  ndNumber: 'Level',
  ndPreview: 'Shown as {level}',
  ndCreate: 'Create and open',
  ndCancel: 'Cancel',
  ndCreating: 'Creating...',
  ndCreated: 'Created {level} of "{name}".',
  ndExisting: 'This song already has: {levels}',
  ndLabelTaken: 'This song already has a {label} chart. You can still create another one.',
  ndNumberInvalid: 'Enter a level number from {min} to {max}, in steps of 0.1.',

  // ---- locked elsewhere ----
  leTitle: '"{name}" is open in another tab',
  leBody: 'Only one tab can edit a chart at a time, so changes from two tabs never mix.',
  leSwitch: 'Switch to it',
  leSwitchHelp: 'Keep editing in the other tab.',
  leEditHere: 'Edit here instead',
  leEditHereHelp: 'The other tab saves the chart, closes it and goes back to its Library.',
  leTakingOver: 'Asking the other tab to save and close the chart...',
  leSwitched:
    'Phiedit asked the other tab to come forward. If your browser did not switch, pick the Phiedit tab with "{name}" in the tab bar.',
  leFailed: 'The other tab did not hand the chart over. Close it there, then try again.',
  leCancel: 'Cancel',
  titleBlink: 'Here: {name}',
  focusRequested: '"{name}" is being edited in this tab. Another tab asked to switch here.',

  // ---- commands ----
  cmdOpen: 'Open chart',
  cmdNewDifficulty: 'New difficulty...',
  cmdDuplicate: 'Duplicate chart',
  cmdExport: 'Export chart as .pez',
  cmdRename: 'Rename chart...',
  cmdTrash: 'Move chart to Trash',
  cmdRestore: 'Restore chart from Trash',
  cmdDeleteForever: 'Delete chart forever',
  cmdEmptyTrash: 'Empty Trash',
  cmdShowTrash: 'Show Trash',
  cmdBackupAll: 'Download all charts as backup',
  cmdProtect: 'Protect my charts from automatic cleanup',
  cmdRecovery: 'Download recovery data',
  noTarget: 'Pick a chart in the Library first.',
  notInTrash: '"{name}" is not in the Trash.',
  deleteNeedsTrash: 'Move the chart to the Trash first. Charts are deleted for good only from the Trash.',
  renameArg: 'New title',
  trashOpenChart: 'Close the chart before moving it to the Trash.',

  // ---- help ----
  helpLibraryTitle: 'Your charts and the Library',
  helpLibraryBody:
    'The Library lists every chart in this browser, grouped by song: charts that use the same song file are difficulties of one song. Open a chart with Enter or a click. The card menu creates a new difficulty (it shares the song and illustration and copies tempo, offset and song details), duplicates, exports, renames or moves a chart to the Trash, where it stays for 30 days. A chart can be edited in one tab at a time: if it is open elsewhere, choose to switch to that tab or to edit here instead.',
  helpBackupTitle: 'Keeping your charts safe',
  helpBackupBody:
    'Every change is saved in this browser as you work, and versions let you go back in time. Browsers may still clear site data when space runs low: use Protect my charts in the Library footer, and Download all as backup now and then. The backup holds every chart as a .pez package plus its phiedit.json file.',
});

export default catalogOf(m);
